import React, { useEffect, useMemo, useRef, useState} from "react";
import ScatterPlotExpression from "./ScatterPlotExpression";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { useProjectStore } from "../../stores/project.store";
import analysisService from "../../services/analysis.service";
import projectsService from "../../services/projects.service";

// Default gene for single cell analysis
// const DEFAULT_SINGLE_CELL_GENE = "AL627309.1";

export interface UmapWithGeneSelectionProps {
  backendResult: any;
  className?: string;
}

const normalizeUmapData = (raw: any): any[] => {
  if (!raw) return [];

  // Case 1: already an array of rows
  if (Array.isArray(raw)) {
    return raw;
  }

  // Case 2: object-of-lists (column -> list), convert to rows
  if (typeof raw === "object") {
    const keys = Object.keys(raw);
    if (keys.length === 0) return [];

    const firstKey = keys[0];
    const length = Array.isArray(raw[firstKey]) ? raw[firstKey].length : 0;

    const rows: any[] = [];
    for (let i = 0; i < length; i++) {
      const row: any = {};
      for (const k of keys) {
        row[k] = raw[k][i];
      }
      rows.push(row);
    }
    return rows;
  }

  // Fallback
  return [];
};

const UmapWithGeneSelection = ({
  backendResult: propBackendResult,
  className = "",
}: UmapWithGeneSelectionProps): React.ReactElement => {
  // ---- Grab everything we need from the store (hooks only here!) ----
  const {
    backendResult: storeBackendResult,
    analysisData,
    setBackendResult,
    setAnalysisError,
    setIsProcessing,
    saveStepResult,
    stepResults,
    activeStep,
    selectedGene,
    setSelectedGene
   } = useProjectStore();
  const requestSequence = useRef(0);
  const [pendingGene, setPendingGene] = useState<string | null>(null);
  const [allUmapData, setAllUmapData] = useState<any[] | null>(null);
  const [loadingAllCells, setLoadingAllCells] = useState(false);

  // Keep the result opened by the panel authoritative. The store may still
  // contain the last QC response and must not overwrite UMAP metadata.
  const backendResult = useMemo(
    () => ({ ...(storeBackendResult ?? {}), ...(propBackendResult ?? {}) }),
    [storeBackendResult, propBackendResult]
  );



  const projectName = analysisData?.projectName;
  const umapArtifactId = backendResult?.artifactId ?? backendResult?.qcArtifactId;

  useEffect(() => {
    setAllUmapData(null);
  }, [umapArtifactId]);

  const handleShowAllCells = async () => {
    if (!projectName || !umapArtifactId || loadingAllCells) return;
    setLoadingAllCells(true);
    try {
      const rows = await projectsService.fetchAllRows(projectName, "Umap", String(umapArtifactId));
      setAllUmapData(rows);
    } catch (error) {
      console.error("Could not load all UMAP cells:", error);
    } finally {
      setLoadingAllCells(false);
    }
  };

  // Gene list and selectedGene derived from backendResult
  const genesList = useMemo(
    () => backendResult?.genesList || [],
    [backendResult?.genesList],
  );

  // const selectedGene: string =
  //   backendResult?.selectedGene || DEFAULT_SINGLE_CELL_GENE;

  // ---- Handle Gene Change ----
  const handleGeneChange = async (_event: any, newValue: string | null) => {
  if (!newValue || newValue === selectedGene) return;
    const requestId = ++requestSequence.current;
    setSelectedGene(newValue);
    setPendingGene(newValue);
    setAnalysisError("");
    setIsProcessing(true);
  if (!projectName) {
    setPendingGene(null);
    setAnalysisError(
      "Project name not available. Please select a project first.",
    );
    setIsProcessing(false);
    return;
  }

  console.log("%c[GEX UI] Gene dropdown changed:", "color:#0af", newValue);
  setIsProcessing(true);

  try {
    const result = await analysisService.fetchGeneExpression(
      projectName,
      newValue,
      backendResult?.currentStep ?? activeStep ?? "Umap",
    );
    if (requestId !== requestSequence.current) return;


    if (result.status !== "success") {
      setAnalysisError(
        result.message || "Failed to load gene expression data",
      );
      setIsProcessing(false);
      return;
    }

    const normalizedTableData = normalizeUmapData(result.tableData);



    const updates = {
      selectedGene: result.selectedGene ?? newValue,
      umapData: normalizedTableData,
      tableData: normalizedTableData,
      genesList: result.genesList ?? backendResult?.genesList,
      artifactId: result.artifactId ?? backendResult?.artifactId,
      artifactStep: result.artifactStep ?? backendResult?.artifactStep,
    };




    const newBackendResult = {
      ...(backendResult || {}),
      ...updates,
    };

    

    setBackendResult(newBackendResult);

    // step history stays the same
    if (activeStep) {
      const currentStepResult = stepResults[activeStep] || {};
      const updatedStepResult = {
        ...currentStepResult,
        ...updates,
      };
      saveStepResult(activeStep, updatedStepResult);
    }

    setIsProcessing(false);
    setPendingGene(null);
  } catch (error) {
    if (requestId !== requestSequence.current) return;
    console.error("Error fetching gene expression data:", error);
    setAnalysisError(
      `Failed to load gene expression data: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    );
    setIsProcessing(false);
    setPendingGene(null);
  }
};

  // ---- Build UMAP data for plotting ----
  const rawUmapData = allUmapData ?? backendResult?.umapData ?? backendResult?.tableData;
  const umapData = normalizeUmapData(rawUmapData);
  const displayGene = backendResult?.selectedGene || selectedGene;

  



  if (!umapData || umapData.length === 0) {
    return (
      <div className={`p-4 text-center ${className}`}>
        <Typography variant="body1" color="text.secondary">
          No UMAP data available. Please run UMAP analysis first.
        </Typography>
      </div>
    );
  }

  return (
    <div className={`space-y-4 h-full flex flex-col ${className}`}>
      <Paper className="flex-shrink-0" variant="outlined" sx={{ p: 1.5, borderColor: "#90caf9", backgroundColor: "#f7fbff" }}>
        <Typography variant="subtitle2" gutterBottom>
          Color UMAP by gene expression
        </Typography>
        <Autocomplete
          value={selectedGene}
          onChange={handleGeneChange}
          options={genesList}
          getOptionLabel={(option) => option || ""}
          isOptionEqualToValue={(option, value) => option === value}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search gene to color UMAP"
              helperText="This control is always available above the UMAP."
              variant="outlined"
              size="small"
              fullWidth
            />
          )}
          disabled={genesList.length === 0}
          freeSolo={false}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
        />
        {Number(backendResult?.totalCells ?? 0) > umapData.length && !allUmapData && (
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1, textTransform: "none" }}
            onClick={handleShowAllCells}
            disabled={loadingAllCells}
          >
            {loadingAllCells
              ? "Loading all cells…"
              : `Show all ${Number(backendResult.totalCells).toLocaleString()} cells`}
          </Button>
        )}
        {allUmapData && (
          <Typography variant="caption" color="success.main" sx={{ display: "block", mt: 1 }}>
            Showing all {allUmapData.length.toLocaleString()} cells.
          </Typography>
        )}
      </Paper>
         
    <div className="relative flex-1 min-h-0">
        <ScatterPlotExpression
          data={umapData}
          xMetric="UMAP1"
          yMetric="UMAP2"
          colorMetric={displayGene}
          title={`UMAP: ${displayGene} Expression`}
          defaultSize={7}
          showSizeSlider={true}
          showLegend={false}
        />
        {pendingGene && (
          <div className="pointer-events-none absolute inset-x-3 top-3 z-10 flex items-center gap-2 rounded-lg bg-white/90 px-3 py-2 text-sm shadow">
            <CircularProgress size={16} />
            Updating color: {pendingGene}
          </div>
        )}
      </div>
    </div>
  );
};

export default UmapWithGeneSelection;
